import { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { useLang } from "../i18n";

export default function PaymentResult() {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const { t } = useLang();
  const isSuccess = pathname.includes("succes");
  const sessionId = params.get("session_id");
  const [state, setState] = useState(isSuccess ? "polling" : "cancelled");
  const [bookingId, setBookingId] = useState(null);

  useEffect(() => {
    if (!isSuccess || !sessionId) return;
    let attempts = 0;
    const timer = setInterval(async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setBookingId(data.booking_id);
          setState("paid");
          clearInterval(timer);
        } else if (attempts > 15) {
          setState("timeout");
          clearInterval(timer);
        }
      } catch {
        if (attempts > 15) {
          setState("timeout");
          clearInterval(timer);
        }
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [isSuccess, sessionId]);

  return (
    <div className="flex min-h-screen flex-col bg-sand" data-testid="payment-result-page">
      <Navbar />
      <main className="mx-auto flex w-full max-w-xl flex-1 items-center px-4 pb-24 pt-36 sm:px-6">
        <div className="w-full rounded-3xl border border-slate-100 bg-white p-12 text-center shadow-xl">
          {state === "polling" && (
            <div data-testid="payment-polling">
              <Loader2 size={48} className="mx-auto animate-spin text-azure" />
              <h1 className="mt-6 font-serif text-2xl font-semibold text-navy">{t("payment.verifying_title")}</h1>
              <p className="mt-2 text-sm text-navy/55">{t("payment.verifying_text")}</p>
            </div>
          )}
          {state === "paid" && (
            <div data-testid="payment-success">
              <CheckCircle2 size={52} className="mx-auto text-emerald-500" />
              <h1 className="mt-6 font-serif text-3xl font-semibold text-navy">{t("payment.success_title")}</h1>
              <p className="mt-3 text-sm leading-relaxed text-navy/60">{t("payment.success_text")}</p>
              {bookingId && (
                <Link to={`/confirmation/${bookingId}`} data-testid="payment-booking-link"
                  className="mt-8 inline-block rounded-full bg-azure px-8 py-3.5 text-sm font-bold text-white transition-colors hover:bg-azure-hover">
                  {t("payment.view_booking")}
                </Link>
              )}
            </div>
          )}
          {(state === "cancelled" || state === "timeout") && (
            <div data-testid="payment-cancelled">
              <XCircle size={52} className="mx-auto text-amber-500" />
              <h1 className="mt-6 font-serif text-3xl font-semibold text-navy">
                {state === "cancelled" ? t("payment.cancelled_title") : t("payment.timeout_title")}
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-navy/60">
                {state === "cancelled" ? t("payment.cancelled_text") : t("payment.timeout_text")}
              </p>
              <Link to="/reservation" data-testid="payment-retry-button"
                className="mt-8 inline-block rounded-full bg-azure px-8 py-3.5 text-sm font-bold text-white transition-colors hover:bg-azure-hover">
                {t("payment.retry")}
              </Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
